import {
    Grid,
    OrthographicCamera,
    PerspectiveCamera,
} from '@react-three/drei';
import { Center, Spinner, Box } from '@chakra-ui/react';
import * as THREE from 'three';
import { useLayoutEffect } from 'react';
import { useView } from '../state/store';
import { useAppStore } from '../state/store';
import { useRenderSettingsStore } from '../state/useRenderSettingsStore';
import { SceneContent } from './SceneContent';
import { SelectionBoxOverlay } from '../tools/selection';
import { SnapOverridePopup } from '../components/viewport/SnapOverridePopup';
import { GripReadoutGhost } from '../components/viewport/GripReadoutGhost';
import { AdvancedLighting } from './AdvancedRendering';
import { RenderSettingsPanel } from '../components/viewport/RenderSettingsPanel';
import { ViewportAxesGizmo } from '../components/viewport/ViewportAxesGizmo';
import { useViewportThemeColors } from '../hooks/useThemeInk';
import { EngineViewport, useViewportContextMenu } from './EngineViewport';
import { AdaptiveCadGrid } from './AdaptiveCadGrid';

/**
 * General drafting viewport (2D / 3D workbenches).
 *
 * Phase A3: this is now a thin configuration shell over EngineViewport.
 * It only decides WHAT the engine shows: the camera branch (orthographic
 * plan vs perspective), the settings-driven lighting/post stack, the CAD
 * grid style, axes, the SceneContent provider and the quick render panel.
 * All canvas/navigation/ViewCube mechanics live in the shared engine.
 *
 * `variant='cad2D'` (2D workbench): a strict 2D wireframe drafting
 * surface — fixed orthographic plan camera (no rotation), no lighting/post/
 * environment/ViewCube pipeline, and the near-black `bg.cad` canvas.
 */
export function Viewport({ variant = 'default' }: { variant?: 'default' | 'cad2D' }) {
    const view = useView();
    const renderSettings = useRenderSettingsStore((s) => s.settings);
    const handleContextMenu = useViewportContextMenu();
    const isCad2D = variant === 'cad2D';
    // Grid tones resolve through the workbench's own token sheet (2D vs 3D),
    // so drawn grid lines match the canvas behind it — including any
    // Settings › Color overrides pinned for the mode.
    const gridColors = useViewportThemeColors(isCad2D ? '2d' : '3d');
    // Corner orientation triad (3D only): shifted further left when the
    // ViewCube occupies the same bottom-right corner.
    const viewCube = useAppStore((s) => s.viewCubeOptions);
    const gizmoRightOffset =
        viewCube.showViewCube && viewCube.position === 'Bottom Right' ? 148 : 16;

    // viewType is app-global; a BIM plan view left it as 'plan' before the
    // user switched workbenches. The interactive 3D workbench always orbits,
    // so clear the leak pre-paint or OrbitControls would mount rotation-locked.
    // (cad2D forces its own fixed plan camera and is unaffected.)
    useLayoutEffect(() => {
        if (isCad2D) return;
        const { view, updateView } = useAppStore.getState();
        if (view.viewType === 'plan') updateView({ viewType: '3d' });
    }, [isCad2D]);

    return (
        <EngineViewport
            camera={
                isCad2D || view.viewType === 'plan' ? (
                    // Z-up world: the plan camera looks straight down the Z
                    // axis from +Z, with Y as up so lookAt is non-degenerate.
                    <OrthographicCamera
                        makeDefault
                        position={[0, 0, 100]}
                        up={[0, 1, 0]}
                        zoom={20}
                        near={0.1}
                        far={10000}
                        onUpdate={(c) => c.lookAt(0, 0, 0)}
                    />
                ) : view.projection === 'orthographic' ? (
                    // Shared projection toggle (status bar / ViewCube): keep
                    // the perspective pose, swap the lens for an ortho one
                    // (same pattern as the BIM viewport).
                    <OrthographicCamera
                        makeDefault
                        position={view.camera.position}
                        up={[0, 0, 1]}
                        zoom={10}
                        near={0.1}
                        far={10000}
                    />
                ) : (
                    <PerspectiveCamera
                        makeDefault
                        position={view.camera.position}
                        up={[0, 0, 1]} // Z-up world (matches BimViewport 3D)
                        fov={view.camera.fov}
                        near={view.camera.near}
                        far={view.camera.far}
                    />
                )
            }
            lighting={isCad2D ? null : <AdvancedLighting settings={renderSettings} />}
            postProcessing={!isCad2D}
            environment={!isCad2D}
            viewCommands={!isCad2D}
            showViewCube={!isCad2D}
            shadows={!isCad2D}
            // 2D drafting canvas resolves through the mode-scoped token so
            // the Settings › Color editor can pin a per-mode override.
            containerBg={isCad2D ? 'var(--color-bg-viewport-2d)' : undefined}
            canvasClassName={isCad2D ? undefined : 'bim-viewport__canvas'}
            controls={{
                target: view.camera.target,
                enableRotate: !isCad2D && view.viewType !== 'plan',
                // Plan = camera on +Z (equator of the Y-referenced polar
                // angle): clamp at phi = π/2, matching BimViewport.
                maxPolarAngle: isCad2D || view.viewType === 'plan' ? Math.PI / 2 : Math.PI,
                minPolarAngle: isCad2D || view.viewType === 'plan' ? Math.PI / 2 : 0,
                mouseButtons: {
                    LEFT: undefined as any, // Selection handled by tools
                    MIDDLE: THREE.MOUSE.PAN,
                    RIGHT: undefined as any, // Context menu
                },
                // 2D/3D-style middle-button pan: panning must follow
                // the screen plane. Ground-plane panning (screenSpacePanning
                // = false) moves the top-down plan camera along Z, which
                // makes the vertical component of a middle-drag invisible.
                screenSpacePanning: true,
            }}
            grid={
                // 2D drafting: AutoCAD-parity adaptive grid — the spacing
                // follows the plan camera zoom (1-2-5 ladder, GRIDMAJOR=5
                // majors, uniform coverage), so one legible grid shows at
                // every zoom instead of two fixed line families stacking.
                isCad2D && view.gridVisible ? (
                    <AdaptiveCadGrid
                        cellColor={gridColors.gridMajor}
                        sectionColor={gridColors.gridSection}
                    />
                ) : (
                    // drei Grid is built for a Y-up plane; rotate onto the XY
                    // ground plane (Z-up convention, see core/scene/axes).
                    view.gridVisible && (
                        <Grid
                            args={[100, 100]}
                            cellSize={1}
                            cellThickness={0.5}
                            cellColor={gridColors.gridMajor}
                            sectionSize={10}
                            sectionThickness={1}
                            sectionColor={gridColors.gridSection}
                            fadeDistance={50}
                            infiniteGrid
                            position={[0, 0, 0]}
                            rotation={[Math.PI / 2, 0, 0]}
                        />
                    )
                )
            }
            axes={
                // 2D keeps the origin axes helper; 3D shows the orientation
                // as the bottom-right corner gizmo instead (see overlay).
                isCad2D && view.axesVisible ? <axesHelper args={[5]} /> : null
            }
            onContextMenu={handleContextMenu}
            suspenseFallback={
                <Center position="absolute" inset="0" bg="bg.canvas">
                    <Spinner size="xl" color="brand.500" />
                </Center>
            }
            overlay={
                <>
                    {/* DOM-only selection rectangle: must live outside the R3F
                        Canvas (three reconciler rejects plain div elements). */}
                    <Box position="absolute" inset="0" pointerEvents="none" zIndex={20}>
                        <SelectionBoxOverlay />
                    </Box>
                    {/* Shift+RMB snap override grid + M2P calculator (Faz
                        11.2): DOM popup, closes on outside click / Escape. */}
                    <SnapOverridePopup />
                    {/* Grip drag coordinate readout balloon (Faz 11.6): DOM
                        layer, rides the pointer while a grip drag runs. */}
                    <GripReadoutGhost />
                    {/* Corner orientation triad: X/Y/Z arms mirroring the live
                        camera, toggled with the shared axes visibility. */}
                    {!isCad2D && view.axesVisible && (
                        <ViewportAxesGizmo live rightOffset={gizmoRightOffset} backgroundToken="--color-bg-viewport-3d" />
                    )}
                    {/* Render Settings quick access (wired to useRenderSettingsStore).
                        Hidden in cad2D: the lightweight wireframe pipeline has no
                        lighting/post settings to tune. */}
                    {!isCad2D && (
                        <Box position="absolute" top={2} right={2} zIndex={10}>
                            <RenderSettingsPanel />
                        </Box>
                    )}
                </>
            }
        >
            <SceneContent cad2D={isCad2D} />
        </EngineViewport>
    );
}
